import { useEffect, useState } from "react";
import {
  BUTTON_ADD,
  BUTTON_CREATE,
  BUTTON_GO_BACK,
  BUTTON_UPDATE,
  LOADING_INFORMATION,
  MODAL_CUSTOM_AUDIENCES_DESCRIPTION,
  MODAL_CUSTOM_AUDIENCES_TITLE,
  MODAL_EXCLUDED_CUSTOM_AUDIENCES_DESCRIPTION,
  MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE,
  SEARCH_BY_NAME_PLACEHOLDER,
  UPDATING_INFORMATION,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import ModalBasicLayout from "../ModalBasicLayout";
import { startGetCustomAudiences } from "../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../lib/redux";
import Loader from "../../Loaders/Loader";
import CustomAudiencesList from "./components/CustomAudiencesList";
import Button from "../../Buttons/Button";
import Box from "@mui/material/Box";
import { openLink } from "../../../utils/string";
import { getCustomAudiencesMeta } from "../../../actions/getters";
import { DialogActions } from "@mui/material";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import { useForm } from "react-hook-form";
import TextField from "../../Form/TextField";
import { DELAY_DEBOUNCE, META } from "../../../utils/constants";
import { useDebounced } from "../../../hooks/useDebounce";
import _ from "lodash";
import { useInView } from "react-intersection-observer";

const ModalCustomAudiences = ({
  modalOpen,
  integrationID,
  adAccountID,
  filteredCustomAudiences = [],
  onCloseModal = () => {},
  onCallbackSave = () => {},
  platform,
  isExcludedAudiences = false,
}) => {
  const { control, watch } = useForm({
    defaultValues: {
      name: "",
    },
  });

  const name = watch("name");

  const [loadLastItem, onChangeLoadLastItem] = useState(false);
  const [lastKey, onChangeLastKey] = useState(undefined);
  const [customAudiences, setCustomAudiences] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const { ref: fromRef, inView: isNearScreen } = useInView({
    triggerOnce: false,
    rootMargin: "20px 0px",
  });

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const fetchCustomAudiences = async ({
    searchName = name,
    shouldReset = false,
    isUpdate = false,
  }) => {
    if (isFetching) return false;

    try {
      setIsFetching(true);
      if (isUpdate) {
        setIsUpdating(true);
      }
      if (shouldReset) {
        setIsLoading(true);
      }

      const response = await dispatch(
        startGetCustomAudiences({
          platform,
          integrationID,
          adAccountID,
          lastKey: shouldReset ? undefined : lastKey,
          onChangeLastKey,
          onChangeLoadLastItem,
          name: searchName,
        })
      );

      if (response.ok) {
        const data = response.data;
        const filteredData = _.uniqBy([...data], "id").filter(
          (v) => !filteredCustomAudiences.includes(v.id)
        );

        setCustomAudiences((prev) =>
          shouldReset
            ? filteredData
            : _.uniqBy([...prev, ...filteredData], "id")
        );
      }

      setIsLoading(false);
      if (isUpdate) {
        setIsUpdating(false);
      }

      return response.ok;
    } finally {
      setIsFetching(false);
    }
  };

  const onGetCustomAudiences = async () => {
    if (loadLastItem) return;
    return fetchCustomAudiences({ searchName: "" });
  };

  const onGetCustomAudiencesByName = useDebounced(
    async (nameProp) => {
      if (!nameProp) {
        onChangeLoadLastItem(false);
        onChangeLastKey(undefined);
        return fetchCustomAudiences({
          shouldReset: true,
          searchName: "",
          forcedLoadLastItem: false,
          forcedLastKey: undefined,
        });
      }

      onChangeLoadLastItem(true);
      return fetchCustomAudiences({ searchName: nameProp, shouldReset: true });
    },
    [DELAY_DEBOUNCE]
  );

  const onUpdateCustomAudiences = async () => {
    return fetchCustomAudiences({ shouldReset: true, isUpdate: true });
  };

  useEffect(() => {
    if (!name) {
      onGetCustomAudiences();
    }
    // eslint-disable-next-line
  }, [isNearScreen]);

  const optionsFiltered = name
    ? customAudiences.filter((v) =>
        v.name.toLowerCase().includes(name.toLowerCase())
      )
    : customAudiences;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={async () => {
        onCloseModal();
      }}
      maxWidth="sm"
      scroll="paper"
      title={t(
        isExcludedAudiences
          ? MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE
          : MODAL_CUSTOM_AUDIENCES_TITLE
      )}
      description={t(
        isExcludedAudiences
          ? MODAL_EXCLUDED_CUSTOM_AUDIENCES_DESCRIPTION
          : MODAL_CUSTOM_AUDIENCES_DESCRIPTION
      )}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1,
          justifyContent: "flex-start",
          width: "100%",
          mt: 3.5,
          mb: 2,
        }}
      >
        <Button
          disabled={isLoading}
          color="secondary"
          startIcon={<CachedIcon />}
          onClick={onUpdateCustomAudiences}
          sx={{
            flex: 1,
            width: "100%",
          }}
        >
          {t(BUTTON_UPDATE)}
        </Button>
        {platform === META && (
          <Button
            color="secondary"
            type="button"
            disabled={isLoading}
            onClick={() => openLink(dispatch(getCustomAudiencesMeta()))}
            sx={{
              flex: 1,
              width: "100%",
            }}
          >
            {t(BUTTON_CREATE)}
          </Button>
        )}
      </Box>

      {platform === META && (
        <TextField
          name="name"
          variant="filled"
          fullWidth={true}
          placeholder={t(SEARCH_BY_NAME_PLACEHOLDER)}
          startIcon={<SearchIcon sx={{ color: "#999999 !important" }} />}
          actionsChange={(name) => {
            setIsLoading(true);
            onGetCustomAudiencesByName(name);
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              height: "35px",
            },
            "& .MuiInputBase-input": {
              padding: "6px 12px",
            },
          }}
          control={control}
        />
      )}

      {isLoading && (
        <Loader
          hasMessage={true}
          message={t(isUpdating ? UPDATING_INFORMATION : LOADING_INFORMATION)}
        />
      )}

      {!isLoading && (
        <CustomAudiencesList
          selectedItems={selectedItems}
          onChangeSelectedItems={setSelectedItems}
          customAudiences={optionsFiltered}
          lastItemRef={fromRef}
          loadLastItem={loadLastItem}
          platform={platform}
        />
      )}
      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_GO_BACK,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: selectedItems.length === 0,
            text: BUTTON_ADD,
            onClick: async () => {
              onCallbackSave(selectedItems);
            },
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalCustomAudiences;
